import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { Asset, Booklet, PhotoRef, SlotKind, SlotSpec, Template, TemplateOptions } from '../lib/types';
import { useToast } from '../components/ui';
import { useProjectContext } from './ProjectLayout';

type SlotTarget =
  | { kind: 'cover'; key: keyof Booklet['covers'] }
  | { kind: 'day'; dayIndex: number; slot: string }
  | { kind: 'experience'; index: number }
  | { kind: 'map' };

export function DesignPage() {
  const { project, assets, update, reloadAssets, setProject } = useProjectContext();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [options, setOptions] = useState<TemplateOptions | null>(null);
  useEffect(() => {
    api.listTemplates().then(setTemplates).catch(() => {});
    api.templateOptions().then(setOptions).catch(() => {});
  }, []);
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [target, setTarget] = useState<SlotTarget | null>(null);
  const [uploading, setUploading] = useState(false);
  const [picking, setPicking] = useState<SlotTarget | null>(null);
  const [mapKey, setMapKey] = useState(0);
  const [mapInfo, setMapInfo] = useState<{ provider: string; distanceKm: number | null; warnings: string[] } | null>(null);
  const b = project.content;

  const citiesKey = b.route.cities.join(',');
  useEffect(() => {
    if (b.route.map.mode !== 'generate') return;
    setMapInfo(null);
    api.routeMapInfo(project.id, mapKey > 0).then(setMapInfo).catch(() => {});
  }, [project.id, citiesKey, b.route.map.mode, mapKey]);

  const assetById = (id: string | null) => assets.find((a) => a.id === id) ?? null;

  // The preview sends us here with #slot-<id>; scroll to it and flash it.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('flash');
    const t = window.setTimeout(() => el.classList.remove('flash'), 2200);
    return () => window.clearTimeout(t);
  }, [project.id, b.days.length, b.experience.length]);

  const setRef = (t: SlotTarget, patch: Partial<PhotoRef>) => {
    update((d) => {
      if (t.kind === 'cover') {
        return { ...d, covers: { ...d.covers, [t.key]: { ...d.covers[t.key], ...patch } } };
      }
      if (t.kind === 'map') {
        return { ...d, route: { ...d.route, map: { ...d.route.map, assetId: patch.assetId ?? null } } };
      }
      if (t.kind === 'experience') {
        return { ...d, experience: d.experience.map((e, i) => (i === t.index ? { ...e, photo: { ...e.photo, ...patch } } : e)) };
      }
      return {
        ...d,
        days: d.days.map((day, i) => (i !== t.dayIndex ? day : {
          ...day,
          photos: day.photos.map((p) => (p.slot === t.slot ? { ...p, ...patch } : p)),
        })),
      };
    });
  };

  /** Which template slot a card fills, so we can show the size it prints at. */
  const slotKind = (t: SlotTarget): SlotKind =>
    t.kind === 'map' ? 'map'
      : t.kind === 'day' ? 'day'
        : t.kind === 'experience' ? 'experience'
          : t.key === 'cover' ? 'cover' : 'full';

  const specFor = (t: SlotTarget): SlotSpec | null => options?.photoSlots?.[slotKind(t)] ?? null;

  /** The photo a slot currently holds, so the picker can mark it. */
  const currentAssetId = (t: SlotTarget): string | null => {
    if (t.kind === 'cover') return b.covers[t.key].assetId;
    if (t.kind === 'map') return b.route.map.assetId;
    if (t.kind === 'experience') return b.experience[t.index]?.photo.assetId ?? null;
    return b.days[t.dayIndex]?.photos.find((p) => p.slot === t.slot)?.assetId ?? null;
  };

  /** Plain-language warning when a photo does not suit the slot it was put in. */
  const sizeWarning = (asset: Asset, spec: SlotSpec | null): string | null => {
    if (!spec) return asset.width < 1240 ? 'Small for print' : null;
    if (asset.width < spec.widthPx * 0.75 || asset.height < spec.heightPx * 0.75) {
      return `Smaller than the ${spec.widthPx}×${spec.heightPx}px this slot prints at`;
    }
    const want = spec.widthPx / spec.heightPx;
    const have = asset.width / asset.height;
    const off = Math.abs(have - want) / want;
    if (off > 0.15) {
      return have > want ? 'Wider than the slot — the sides are cropped' : 'Taller than the slot — the top and bottom are cropped';
    }
    return null;
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const asset = await api.uploadAsset(project.id, file);
      await reloadAssets();
      if (target) setRef(target, { assetId: asset.id });
      const warning = target ? sizeWarning(asset, specFor(target)) : (asset.lowRes ? 'Small for print' : null);
      toast.push(warning ? `Uploaded — ${warning.toLowerCase()}` : 'Photo uploaded');
    } catch (e) {
      toast.push((e as Error).message, true);
    } finally {
      setUploading(false);
    }
  };

  const slotId = (t: SlotTarget) =>
    t.kind === 'cover' ? `slot-${t.key}`
      : t.kind === 'map' ? 'slot-map'
        : t.kind === 'experience' ? `slot-exp-${t.index}`
          : `slot-day-${b.days[t.dayIndex]?.day ?? t.dayIndex}`;

  const slotCard = (label: string, ref: PhotoRef, t: SlotTarget) => {
    const asset = assetById(ref.assetId);
    const spec = specFor(t);
    const warning = asset ? sizeWarning(asset, spec) : null;
    const usesTemplate = (t.kind === 'cover' || t.kind === 'map') && b.useTemplatePhotos;
    return (
      <div className="slot-card" key={label} id={slotId(t)}>
        <div className="thumb">
          {asset
            ? <img src={asset.url} alt={label} style={{ objectPosition: `${ref.focalX * 100}% ${ref.focalY * 100}%` }} />
            : <div className="placeholder">{label}<br />{usesTemplate ? 'sample photo' : 'no photo yet'}</div>}
        </div>
        <div className="body">
          <div className="label">{label}</div>
          {spec && (
            <div className="slot-size" title={spec.note}>
              Best size {spec.widthPx} × {spec.heightPx} px · {spec.ratio}
            </div>
          )}
          <div className="controls">
            <button className="btn small" onClick={() => { setTarget(t); fileRef.current?.click(); }}>Upload</button>
            <button className="btn small" onClick={() => { setTarget(t); setPicking(t); }}>Library</button>
            {asset && <button className="btn small danger" onClick={() => setRef(t, { assetId: null })}>Clear</button>}
          </div>
          {asset && (
            <div style={{ marginTop: 8 }}>
              <div className="sub" style={{ fontSize: 11 }}>Focal point</div>
              <input type="range" min={0} max={1} step={0.05} value={ref.focalX}
                onChange={(e) => setRef(t, { focalX: Number(e.target.value) })} />
              <input type="range" min={0} max={1} step={0.05} value={ref.focalY}
                onChange={(e) => setRef(t, { focalY: Number(e.target.value) })} />
              <div className="sub" style={{ fontSize: 11, marginTop: 4 }}>Zoom {Math.round((ref.zoom ?? 1) * 100)}%</div>
              <input type="range" min={1} max={2.5} step={0.05} value={ref.zoom ?? 1}
                onChange={(e) => setRef(t, { zoom: Number(e.target.value) })} />
              <div className="sub" style={{ fontSize: 11 }}>{asset.width}×{asset.height}px</div>
              {warning && <div className="slot-warning">{warning}</div>}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
          e.target.value = '';
        }}
      />

      <div className="panel" id="pages">
        <h2 className="section">Template &amp; pages</h2>
        <div className="grid-2">
          <div className="field">
            <label>Template</label>
            <select
              value={project.templateId}
              onChange={async (e) => {
                try {
                  const saved = await api.updateProject(project.id, { templateId: e.target.value });
                  setProject({ ...project, templateId: saved.templateId, templateVersion: saved.templateVersion });
                  toast.push('Template changed');
                } catch (err) {
                  toast.push((err as Error).message, true);
                }
              }}
            >
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div className="hint">Colours, fonts and page order come from the template.</div>
          </div>
          <div className="field">
            <label>Text over the package photo</label>
            <div className="row colour-pick">
              <input
                type="color"
                value={hexOr(b.photoTextColor, '#073D2E')}
                onChange={(e) => update((d) => ({ ...d, photoTextColor: e.target.value.toUpperCase() }))}
              />
              <input
                className="search hex"
                value={b.photoTextColor}
                spellCheck={false}
                onChange={(e) => {
                  const v = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
                  update((d) => ({ ...d, photoTextColor: v.toUpperCase().slice(0, 7) }));
                }}
              />
              {['#073D2E', '#FDF6EE', '#FFFFFF', '#D7E026', '#0E1A16'].map((hex) => (
                <button
                  key={hex}
                  className={`swatch-btn ${b.photoTextColor?.toUpperCase() === hex ? 'on' : ''}`}
                  style={{ background: hex }}
                  title={hex}
                  onClick={() => update((d) => ({ ...d, photoTextColor: hex }))}
                />
              ))}
            </div>
            <div className="hint">Pick a colour that reads against the photo on “Pick your package”. A pale colour is printed with a soft shadow.</div>
          </div>

          <div className="field">
            <label>Sample photos</label>
            <label className="row" style={{ gap: 8, textTransform: 'none', letterSpacing: 0, fontSize: 13, color: 'var(--text)' }}>
              <input type="checkbox" checked={b.useTemplatePhotos} onChange={(e) => update((d) => ({ ...d, useTemplatePhotos: e.target.checked }))} />
              Show the template’s own photos in empty full-page slots
            </label>
            <div className="hint">Handy for a draft; turn it off before sending a booklet to clients.</div>
          </div>
        </div>
        {options && (
          <>
            <div className="sub" style={{ margin: '4px 0 8px' }}>Pages in this booklet (cover, day pages and the closing page always print)</div>
            <div className="page-toggles">
              {options.hideablePages.map((pg) => {
                const on = !b.hiddenPages.includes(pg.id);
                return (
                  <label key={pg.id} className={`page-toggle ${on ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => update((d) => ({
                        ...d,
                        hiddenPages: e.target.checked ? d.hiddenPages.filter((x) => x !== pg.id) : [...d.hiddenPages, pg.id],
                      }))}
                    />
                    {pg.label}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="panel">
        <h2 className="section">Route map</h2>
        <div className="row" style={{ marginBottom: 12 }}>
          {(['generate', 'upload'] as const).map((mode) => (
            <button
              key={mode}
              className={`btn small ${b.route.map.mode === mode ? 'primary' : ''}`}
              onClick={() => update((d) => ({ ...d, route: { ...d.route, map: { ...d.route.map, mode } } }))}
            >
              {mode === 'generate' ? 'Generate from cities' : 'Upload an image'}
            </button>
          ))}
          <span className="sub">
            {b.route.map.mode === 'generate'
              ? 'Type the cities (or full addresses) in step 2 — the road route and the map are drawn for you.'
              : b.useTemplatePhotos ? 'Upload a map screenshot. The sample map is shown until you do.' : 'Upload a map screenshot.'}
          </span>
        </div>

        {b.route.map.mode === 'generate' ? (
          <>
            <img
              src={`/api/projects/${project.id}/route-map?v=${encodeURIComponent(citiesKey + JSON.stringify(b.route.map.labelOverrides))}&k=${mapKey}`}
              alt="Generated route map"
              style={{ width: '100%', maxWidth: 620, borderRadius: 8, border: '1px solid var(--line)', background: '#e4f1e3' }}
            />
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn small" onClick={() => setMapKey((k) => k + 1)}>Rebuild map</button>
              {mapInfo ? (
                <span className="sub">
                  {mapInfo.provider === 'google' && 'Google Maps'}
                  {mapInfo.provider === 'osm' && 'OpenStreetMap + OSRM road routing'}
                  {mapInfo.provider === 'offline' && 'Offline — straight lines only'}
                  {mapInfo.distanceKm != null && ` · road distance ≈ ${mapInfo.distanceKm.toLocaleString('en-US')} km`}
                </span>
              ) : <span className="sub">Building map…</span>}
            </div>
            {mapInfo?.warnings.map((w, i) => <div key={i} className="issue" style={{ marginTop: 8 }}>{w}</div>)}
            {mapInfo && mapInfo.provider !== 'google' && uniqueCities(b.route.cities).length > 0 && (
              <details className="nudges">
                <summary className="sub">City names overlapping? Move a label</summary>
                <div className="nudge-grid">
                  {uniqueCities(b.route.cities).map((city) => {
                    const o = b.route.map.labelOverrides.find((x) => x.city.toLowerCase() === city.toLowerCase());
                    const nudge = (dx: number, dy: number) => update((d) => {
                      const rest = d.route.map.labelOverrides.filter((x) => x.city.toLowerCase() !== city.toLowerCase());
                      const cur = o ?? { city, dx: 0, dy: 0 };
                      const next = { city, dx: cur.dx + dx, dy: cur.dy + dy };
                      return { ...d, route: { ...d.route, map: { ...d.route.map, labelOverrides: next.dx || next.dy ? [...rest, next] : rest } } };
                    });
                    return (
                      <div className="nudge" key={city}>
                        <span style={{ flex: 1 }}>{city}</span>
                        <button className="btn small ghost" onClick={() => nudge(-14, 0)} aria-label="left">&larr;</button>
                        <button className="btn small ghost" onClick={() => nudge(0, -14)} aria-label="up">&uarr;</button>
                        <button className="btn small ghost" onClick={() => nudge(0, 14)} aria-label="down">&darr;</button>
                        <button className="btn small ghost" onClick={() => nudge(14, 0)} aria-label="right">&rarr;</button>
                        {o && <button className="btn small ghost" onClick={() => nudge(-o.dx, -o.dy)}>reset</button>}
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
            {mapInfo && mapInfo.provider === 'osm' && (
              <div className="sub" style={{ marginTop: 8 }}>
                For the exact Google Maps look of the template, set <code>GOOGLE_MAPS_API_KEY</code> in the backend .env.
              </div>
            )}
          </>
        ) : (
          <div className="slot-grid" style={{ maxWidth: 260 }}>
            {slotCard('Route map image', {
              slot: 'map', assetId: b.route.map.assetId, focalX: .5, focalY: .5, zoom: 1,
            }, { kind: 'map' })}
          </div>
        )}
      </div>

      <div className="panel">
        <h2 className="section">Full-page photos</h2>
        <div className="sub" style={{ marginBottom: 10 }}>
          {b.useTemplatePhotos
            ? 'This booklet started from the sample, so empty slots show the sample photos until you replace them.'
            : 'One photo per page. Empty slots stay blank in the PDF.'}
        </div>
        <div className="slot-grid">
          {slotCard('Cover stamp', b.covers.cover, { kind: 'cover', key: 'cover' })}
          {slotCard('Divider A (after schedule)', b.covers.dividerA, { kind: 'cover', key: 'dividerA' })}
          {slotCard('Divider B (before experience)', b.covers.dividerB, { kind: 'cover', key: 'dividerB' })}
          {slotCard('Pick your package', b.covers.pick, { kind: 'cover', key: 'pick' })}
          {slotCard('Closing', b.covers.closing, { kind: 'cover', key: 'closing' })}
        </div>
      </div>

      <div className="panel">
        <h2 className="section">Day photos</h2>
        {b.days.map((day, dayIndex) => (
          <div key={dayIndex} style={{ marginBottom: 18 }}>
            <div className="sub" style={{ marginBottom: 8 }}>
              <span className="mono" style={{ color: 'var(--lime)' }}>DAY {day.day}</span> {day.title}
            </div>
            <div className="slot-grid">
              {day.photos.filter((photo) => photo.slot === 'main').map((photo) => slotCard(
                `Day ${day.day} stamp photo`,
                photo,
                { kind: 'day', dayIndex, slot: photo.slot },
              ))}
            </div>
          </div>
        ))}
        {!b.days.length && <div className="sub">No day pages yet — add them on the Content tab.</div>}
        <div className="sub" style={{ marginTop: 6 }}>
          The template shows one stamp photo per day; the first two days share a text-only page and get no photo.
        </div>
      </div>

      <div className="panel">
        <h2 className="section">Experience photos</h2>
        <div className="slot-grid">
          {b.experience.map((item, index) => slotCard(item.label || `Experience ${index + 1}`, item.photo, { kind: 'experience', index }))}
        </div>
        {!b.experience.length && <div className="sub">No experience items yet — add them on the Content tab.</div>}
      </div>

      {picking && (
        <div className="modal-back" onClick={() => setPicking(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ marginBottom: 10 }}>
              <h2 className="section" style={{ margin: 0 }}>Choose a photo</h2>
              <div style={{ flex: 1 }} />
              <button className="btn small ghost" onClick={() => setPicking(null)}>Close</button>
            </div>
            {(() => {
              const spec = specFor(picking);
              return spec ? (
                <div className="sub" style={{ marginBottom: 10 }}>
                  This slot prints at {spec.widthPx} × {spec.heightPx} px ({spec.ratio}){spec.note ? ` — ${spec.note}` : ''}
                </div>
              ) : null;
            })()}
            {assets.length ? (
              <div className="library">
                {assets.map((asset) => {
                  const warning = sizeWarning(asset, specFor(picking));
                  return (
                    <div
                      key={asset.id}
                      className={`item ${currentAssetId(picking) === asset.id ? 'selected' : ''}`}
                      onClick={() => {
                        setRef(picking, { assetId: asset.id });
                        setPicking(null);
                        toast.push(warning ? `Photo placed — ${warning.toLowerCase()}` : 'Photo placed');
                      }}
                    >
                      <img src={asset.url} alt={asset.filename} />
                      <div className="cap">{asset.filename}</div>
                      <div className={`cap ${warning ? 'warn-text' : ''}`}>
                        {asset.width}×{asset.height}{warning ? ' · does not fit well' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="sub">No photos in this booklet yet — upload one below.</div>
            )}
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn primary small" onClick={() => { setTarget(picking); setPicking(null); fileRef.current?.click(); }}>
                Upload a new photo
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <h2 className="section">
          Media library {uploading && <span className="sub">· uploading…</span>}
        </h2>
        <div className="row" style={{ marginBottom: 12 }}>
          <button className="btn small" onClick={() => { setTarget(null); fileRef.current?.click(); }}>Upload photo</button>
          <span className="sub">
            {target ? 'Click a photo below to drop it into the selected slot.' : 'Select a slot above first to assign a photo.'}
          </span>
        </div>
        <div className="library">
          {assets.map((asset) => (
            <div
              key={asset.id}
              className="item"
              onClick={() => {
                if (!target) return toast.push('Pick a slot first (Library button on a slot)', true);
                setRef(target, { assetId: asset.id });
                toast.push('Photo placed');
              }}
            >
              <img src={asset.url} alt={asset.filename} />
              <div className="cap">{asset.filename}</div>
            </div>
          ))}
          {!assets.length && <div className="sub">No photos uploaded yet.</div>}
        </div>
        {assets.length > 0 && <AssetCleanup assets={assets} projectId={project.id} onDone={reloadAssets} />}
      </div>
    </>
  );
}

function AssetCleanup({ assets, projectId, onDone }: { assets: Asset[]; projectId: string; onDone: () => Promise<void> }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <div style={{ marginTop: 14 }}>
      <div className="sub" style={{ marginBottom: 6 }}>Remove a photo from the library</div>
      <div className="row">
        {assets.map((asset) => (
          <span className="chip" key={asset.id}>
            {asset.filename}
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.deleteAsset(projectId, asset.id);
                  await onDone();
                  toast.push('Photo removed');
                } catch (e) {
                  const message = (e as Error).message;
                  if (message.includes('still used') && window.confirm(`${message}\n\nDelete anyway?`)) {
                    await api.deleteAsset(projectId, asset.id, true);
                    await onDone();
                  } else {
                    toast.push(message, true);
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function uniqueCities(cities: string[]): string[] {
  const seen = new Set<string>();
  return cities.filter((c) => {
    const k = c.trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** A colour input needs a valid hex even while the field is half-typed. */
function hexOr(value: string | undefined, fallback: string): string {
  return value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}
